import * as React from 'react'

import { observer } from 'mobx-react'

import { Button } from '~/client/src/shared/components/Button/Button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  type DialogPortalContainer,
} from '~/client/src/shared/components/shadcn-ui/dialog'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { cn } from '~/client/src/shared/utils/cn'

export type AlertDialogIconTone = 'muted' | 'success' | 'destructive'

const ICON_TONE_CLASS: Record<AlertDialogIconTone, string> = {
  muted: 'tw:bg-muted tw:text-muted-foreground',
  success: 'tw:bg-success-soft tw:text-success',
  destructive: 'tw:bg-destructive/10 tw:text-destructive',
}

interface IAlertDialogIconProps {
  tone?: AlertDialogIconTone
  children: React.ReactNode
}

/**
 * The tinted 40px tile that holds an alert's leading icon. Exported so a
 * surface that can't be a Dialog (e.g. a mobile Drawer) renders the same tile.
 */
export function AlertDialogIcon({
  tone = 'muted',
  children,
}: IAlertDialogIconProps) {
  return (
    <div
      aria-hidden
      data-slot="alert-dialog-icon"
      className={cn(
        'tw:flex tw:size-10 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-md tw:[&_svg:not([class*=size-])]:size-6',
        ICON_TONE_CLASS[tone],
      )}
    >
      {children}
    </div>
  )
}

interface IProps {
  isOpen: boolean
  title?: string
  onClose: () => void
  buttonTitle?: string
  className?: string
  portalContainer?: DialogPortalContainer
  /**
   * Optional leading icon. When set, the alert uses the media layout: the
   * icon tile beside the title and body, and an outline button aligned to
   * the end. Without it the alert keeps its centered, primary-button layout.
   */
  icon?: React.ReactNode
  /** Tint of the icon tile. Defaults to `muted`. Ignored without `icon`. */
  iconTone?: AlertDialogIconTone
}

@observer
export default class AlertDialog extends React.Component<IProps> {
  public render() {
    const { isOpen, onClose, className, portalContainer } = this.props

    return (
      <Dialog
        open={isOpen}
        onOpenChange={open => {
          if (!open) {
            onClose()
          }
        }}
        disablePointerDismissal
      >
        <DialogContent
          showCloseButton={false}
          className={className}
          portalContainer={portalContainer}
        >
          {this.props.icon ? this.renderMedia() : this.renderPlain()}
        </DialogContent>
      </Dialog>
    )
  }

  private renderPlain() {
    const { onClose, buttonTitle, title, children } = this.props

    return (
      <>
        {title ? (
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
        ) : (
          <DialogTitle className="tw:sr-only">
            {Localization.translator.alert}
          </DialogTitle>
        )}

        <div className="tw:text-body-md tw:text-foreground">{children}</div>

        <DialogFooter className="tw:sm:justify-center">
          <Button size="lg" onClick={onClose}>
            {buttonTitle || Localization.translator.ok}
          </Button>
        </DialogFooter>
      </>
    )
  }

  private renderMedia() {
    const { onClose, buttonTitle, title, children, icon, iconTone } = this.props

    return (
      <>
        <div className="tw:flex tw:items-start tw:gap-4">
          <AlertDialogIcon tone={iconTone}>{icon}</AlertDialogIcon>
          <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-1.5">
            <DialogTitle className={title ? undefined : 'tw:sr-only'}>
              {title || Localization.translator.alert}
            </DialogTitle>
            <DialogDescription render={<div />}>{children}</DialogDescription>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {buttonTitle || Localization.translator.ok}
          </Button>
        </DialogFooter>
      </>
    )
  }
}
